import { getTierCapabilities, type TierKey } from "@reach/core";

export type PlanDetail = {
  name: string;
  tagline: string;
  price: number;
  annualPrice: number;
  description: string;
  features: string[];
  audience: string;
  trial: string | null;
};

export type PlanComparisonSection = {
  title: string;
  rows: Array<{
    feature: string;
    tooltip?: string;
    values: Record<TierKey, boolean | string>;
  }>;
};

export const PLAN_DETAILS: Record<TierKey, PlanDetail> = {
  free: {
    name: "Reach Free",
    tagline: "Get started in seconds",
    price: 0,
    annualPrice: 0,
    description: "A complete, working widget with no visitor caps, free scheduling, and a real analytics teaser.",
    audience: "For stores that want a reliable contact widget without artificial limits.",
    trial: null,
    features: [
      "Unlimited use with no visitor caps",
      "Dock launcher with your configured channel apps",
      "Business hours, timezone, weekends, and exact placement controls",
      "7-day interaction preview",
      "Default offline flow with your configured apps only",
    ],
  },
  growth: {
    name: "Reach Growth",
    tagline: "Prove your ROI",
    price: 12,
    annualPrice: 10,
    description: "Make the widget yours, unlock the full offline toolkit, and see how visitors actually engage.",
    audience: "For merchants who want stronger branding control and real visitor analytics.",
    trial: null,
    features: [
      "Branding removal and full button styling",
      "Dock + Container launch styles",
      "Custom avatars and app labels",
      "Lunch breaks and holiday calendar",
      "Device targeting (desktop/mobile)",
      "Offline strategies: hide widget or show Back in X",
      "90-day visitor analytics with widget opens, unique visitors, device split (desktop/mobile), and return rate",
    ],
  },
  agent: {
    name: "Reach Agent",
    tagline: "Your 24/7 smart assistant",
    price: 39,
    annualPrice: 33,
    description: "Add a smart assistant with a knowledge base, website auto-scan, and the analytics needed to improve answers over time.",
    audience: "For stores ready to automate support and keep helping customers after hours.",
    trial: null,
    features: [
      "Everything in Growth, plus:",
      "Chat-first launch style that opens the assistant immediately",
      "Smart assistant with keyword and full-text search",
      "Website auto-scan that pulls key store pages into your chatbot knowledge base for a faster setup",
      "Manual training tools for adding custom questions, answers, policies, and product guidance in your own words",
      "Chatbot analytics: conversations started, messages exchanged, match rate, gaps, top topics, satisfaction, and suggestion clicks",
      "24/7 chatbot-only offline mode with unlimited smart lookups",
    ],
  },
  rep: {
    name: "Reach Rep",
    tagline: "Advanced growth controls",
    price: 99,
    annualPrice: 84,
    description: "Build on Agent with deeper reporting, export controls, and richer workflow customization.",
    audience: "For revenue-focused teams that want more operational visibility and control.",
    trial: null,
    features: [
      "Everything in Agent, plus:",
      "Advanced assistant responses with retrieval over your knowledge base",
      "Advanced analytics, deeper trend reporting, and 12-month retention",
      "CSV export for knowledge base backups and bulk edits",
      "Sales prompting and priority support",
      "5,000 assistant interactions per month",
    ],
  },
  vea: {
    name: "Reach Vea",
    tagline: "Full power, zero limits",
    price: 299,
    annualPrice: 254,
    description: "Unlock the full Reach stack with the highest limits, premium modules, and the longest retention window.",
    audience: "For high-volume brands that need the fullest Reach setup with zero usage ceilings.",
    trial: null,
    features: [
      "Everything in Rep, plus:",
      "Unlimited assistant interactions with the full advanced response stack",
      "Vea-only analytics and premium modules",
      "Product designer access",
      "24-month analytics retention",
    ],
  },
};

export const formatRetentionLabel = (days: number) => {
  if (days >= 730) return "24 months";
  if (days >= 365) return "12 months";
  return `${days} days`;
};

const hasOfflineChatbotOnly = (tier: TierKey) => getTierCapabilities(tier).canUseKnowledgeBase;
const hasHolidayCalendar = (tier: TierKey) => tier !== "free";
const hasLunchBreaks = (tier: TierKey) => tier !== "free";
const hasCustomIdentity = (tier: TierKey) => getTierCapabilities(tier).canCustomizeButtonColor;
const getLaunchStylesLabel = (tier: TierKey) => {
  if (tier === "free") return "Dock";
  if (tier === "growth") return "Dock + Container";
  return "Dock + Container + Chat-first";
};
const getAssistantEngineLabel = (tier: TierKey) => {
  const capabilities = getTierCapabilities(tier);
  if (!capabilities.canUseKnowledgeBase) return "—";
  if (capabilities.canUseGenerativeAI && capabilities.canUseRAG) return "Advanced responses + retrieval";
  return "Keyword + full-text search";
};
const getInteractionLimitLabel = (tier: TierKey) => {
  const capabilities = getTierCapabilities(tier);
  if (!capabilities.canUseKnowledgeBase) return "—";
  if (capabilities.maxAIInteractions === 0) return "Unlimited";
  return capabilities.maxAIInteractions.toLocaleString();
};

export const PLAN_COMPARISON_SECTIONS: PlanComparisonSection[] = [
  {
    title: "Apps & Availability",
    rows: [
      {
        feature: "Unlimited widget use",
        tooltip: "Reach never caps visitor volume or the number of configured apps you use.",
        values: { free: "Unlimited", growth: "Unlimited", agent: "Unlimited", rep: "Unlimited", vea: "Unlimited" },
      },
      {
        feature: "Business hours + timezone",
        tooltip: "Store status follows IANA timezone scheduling on every plan.",
        values: { free: true, growth: true, agent: true, rep: true, vea: true },
      },
      {
        feature: "Lunch breaks",
        values: { free: false, growth: hasLunchBreaks("growth"), agent: hasLunchBreaks("agent"), rep: hasLunchBreaks("rep"), vea: hasLunchBreaks("vea") },
      },
      {
        feature: "Holiday calendar",
        values: { free: false, growth: hasHolidayCalendar("growth"), agent: hasHolidayCalendar("agent"), rep: hasHolidayCalendar("rep"), vea: hasHolidayCalendar("vea") },
      },
      {
        feature: "Offline behavior",
        tooltip: "Free uses your configured apps only. Growth unlocks the full offline behavior toolkit. Agent+ adds chatbot-only mode.",
        values: {
          free: "Channels (configured apps) only",
          growth: "All offline strategies",
          agent: "Growth strategies + chatbot-only mode",
          rep: "Growth strategies + chatbot-only mode",
          vea: "Growth strategies + chatbot-only mode",
        },
      },
    ],
  },
  {
    title: "Appearance & Control",
    rows: [
      {
        feature: "Launch styles",
        tooltip: "Free uses the dock launcher, Growth unlocks the container drawer, and Agent+ adds Chat-first.",
        values: {
          free: getLaunchStylesLabel("free"),
          growth: getLaunchStylesLabel("growth"),
          agent: getLaunchStylesLabel("agent"),
          rep: getLaunchStylesLabel("rep"),
          vea: getLaunchStylesLabel("vea"),
        },
      },
      {
        feature: "Position + exact margins",
        tooltip: "Left/right placement and precise desktop/mobile offsets are available on all tiers.",
        values: { free: true, growth: true, agent: true, rep: true, vea: true },
      },
      {
        feature: "Button colors and styling",
        values: {
          free: false,
          growth: getTierCapabilities("growth").canCustomizeButtonColor,
          agent: getTierCapabilities("agent").canCustomizeButtonColor,
          rep: getTierCapabilities("rep").canCustomizeButtonColor,
          vea: getTierCapabilities("vea").canCustomizeButtonColor,
        },
      },
      {
        feature: "Desktop / mobile button sizing",
        values: {
          free: getTierCapabilities("free").canCustomizeButtonSize,
          growth: getTierCapabilities("growth").canCustomizeButtonSize,
          agent: getTierCapabilities("agent").canCustomizeButtonSize,
          rep: getTierCapabilities("rep").canCustomizeButtonSize,
          vea: getTierCapabilities("vea").canCustomizeButtonSize,
        },
      },
      {
        feature: "Custom avatars + app labels",
        values: {
          free: false,
          growth: hasCustomIdentity("growth"),
          agent: hasCustomIdentity("agent"),
          rep: hasCustomIdentity("rep"),
          vea: hasCustomIdentity("vea"),
        },
      },
      {
        feature: "Device targeting (desktop/mobile)",
        values: {
          free: getTierCapabilities("free").hasDeviceTargeting,
          growth: getTierCapabilities("growth").hasDeviceTargeting,
          agent: getTierCapabilities("agent").hasDeviceTargeting,
          rep: getTierCapabilities("rep").hasDeviceTargeting,
          vea: getTierCapabilities("vea").hasDeviceTargeting,
        },
      },
      {
        feature: "Remove Reach branding",
        values: {
          free: getTierCapabilities("free").canRemoveBranding,
          growth: getTierCapabilities("growth").canRemoveBranding,
          agent: getTierCapabilities("agent").canRemoveBranding,
          rep: getTierCapabilities("rep").canRemoveBranding,
          vea: getTierCapabilities("vea").canRemoveBranding,
        },
      },
    ],
  },
  {
    title: "Analytics",
    rows: [
      {
        feature: "Analytics retention",
        values: {
          free: formatRetentionLabel(getTierCapabilities("free").analyticsRetentionDays),
          growth: formatRetentionLabel(getTierCapabilities("growth").analyticsRetentionDays),
          agent: formatRetentionLabel(getTierCapabilities("agent").analyticsRetentionDays),
          rep: formatRetentionLabel(getTierCapabilities("rep").analyticsRetentionDays),
          vea: formatRetentionLabel(getTierCapabilities("vea").analyticsRetentionDays),
        },
      },
      {
        feature: "Interaction teaser",
        tooltip: "Free includes the single interaction preview number that creates the Growth upgrade moment.",
        values: { free: "7-day teaser", growth: true, agent: true, rep: true, vea: true },
      },
      {
        feature: "Visitor analytics",
        tooltip: "Widget opens, unique visitors, device split (desktop/mobile), and return visitor rate.",
        values: { free: false, growth: true, agent: true, rep: true, vea: true },
      },
      {
        feature: "Chatbot analytics",
        tooltip: "Conversations started, messages exchanged, answer match rate, top topics, unanswered questions, satisfaction, and suggestion usage.",
        values: {
          free: getTierCapabilities("free").hasChatbotAnalytics,
          growth: getTierCapabilities("growth").hasChatbotAnalytics,
          agent: getTierCapabilities("agent").hasChatbotAnalytics,
          rep: getTierCapabilities("rep").hasChatbotAnalytics,
          vea: getTierCapabilities("vea").hasChatbotAnalytics,
        },
      },
      {
        feature: "Advanced analytics",
        tooltip: "Deeper reporting like richer conversation trends and advanced chatbot metrics.",
        values: {
          free: getTierCapabilities("free").canUseAdvancedAnalytics,
          growth: getTierCapabilities("growth").canUseAdvancedAnalytics,
          agent: getTierCapabilities("agent").canUseAdvancedAnalytics,
          rep: getTierCapabilities("rep").canUseAdvancedAnalytics,
          vea: getTierCapabilities("vea").canUseAdvancedAnalytics,
        },
      },
    ],
  },
  {
    title: "Chatbot & Knowledge Base",
    rows: [
      {
        feature: "Smart assistant + knowledge base",
        tooltip: "Keyword and full-text search over your trained content.",
        values: {
          free: getTierCapabilities("free").canUseKnowledgeBase,
          growth: getTierCapabilities("growth").canUseKnowledgeBase,
          agent: getTierCapabilities("agent").canUseKnowledgeBase,
          rep: getTierCapabilities("rep").canUseKnowledgeBase,
          vea: getTierCapabilities("vea").canUseKnowledgeBase,
        },
      },
      {
        feature: "Assistant engine",
        tooltip: "How the assistant answers from your trained content and knowledge base.",
        values: {
          free: getAssistantEngineLabel("free"),
          growth: getAssistantEngineLabel("growth"),
          agent: getAssistantEngineLabel("agent"),
          rep: getAssistantEngineLabel("rep"),
          vea: getAssistantEngineLabel("vea"),
        },
      },
      {
        feature: "Website auto-scan",
        values: {
          free: getTierCapabilities("free").canUseScraper,
          growth: getTierCapabilities("growth").canUseScraper,
          agent: getTierCapabilities("agent").canUseScraper,
          rep: getTierCapabilities("rep").canUseScraper,
          vea: getTierCapabilities("vea").canUseScraper,
        },
      },
      {
        feature: "24/7 chatbot-only offline mode",
        values: {
          free: hasOfflineChatbotOnly("free"),
          growth: hasOfflineChatbotOnly("growth"),
          agent: hasOfflineChatbotOnly("agent"),
          rep: hasOfflineChatbotOnly("rep"),
          vea: hasOfflineChatbotOnly("vea"),
        },
      },
      {
        feature: "Chatbot lookups and responses",
        values: {
          free: getInteractionLimitLabel("free"),
          growth: getInteractionLimitLabel("growth"),
          agent: getInteractionLimitLabel("agent"),
          rep: getInteractionLimitLabel("rep"),
          vea: getInteractionLimitLabel("vea"),
        },
      },
      {
        feature: "CSV knowledge export",
        tooltip: "Export trained knowledge for backups, bulk edits, or offline review.",
        values: {
          free: getTierCapabilities("free").hasCSVExport,
          growth: getTierCapabilities("growth").hasCSVExport,
          agent: getTierCapabilities("agent").hasCSVExport,
          rep: getTierCapabilities("rep").hasCSVExport,
          vea: getTierCapabilities("vea").hasCSVExport,
        },
      },
    ],
  },
  {
    title: "Advanced Modules",
    rows: [
      {
        feature: "Priority support",
        values: {
          free: getTierCapabilities("free").hasPrioritySupport,
          growth: getTierCapabilities("growth").hasPrioritySupport,
          agent: getTierCapabilities("agent").hasPrioritySupport,
          rep: getTierCapabilities("rep").hasPrioritySupport,
          vea: getTierCapabilities("vea").hasPrioritySupport,
        },
      },
      {
        feature: "Sales prompting",
        tooltip: "Tools for guided sales flows and revenue-focused assistant behavior.",
        values: {
          free: getTierCapabilities("free").hasSalesPrompting,
          growth: getTierCapabilities("growth").hasSalesPrompting,
          agent: getTierCapabilities("agent").hasSalesPrompting,
          rep: getTierCapabilities("rep").hasSalesPrompting,
          vea: getTierCapabilities("vea").hasSalesPrompting,
        },
      },
      {
        feature: "Product designer",
        values: {
          free: getTierCapabilities("free").hasProductDesigner,
          growth: getTierCapabilities("growth").hasProductDesigner,
          agent: getTierCapabilities("agent").hasProductDesigner,
          rep: getTierCapabilities("rep").hasProductDesigner,
          vea: getTierCapabilities("vea").hasProductDesigner,
        },
      },
      {
        feature: "Vea analytics",
        values: {
          free: getTierCapabilities("free").hasVeaAnalytics,
          growth: getTierCapabilities("growth").hasVeaAnalytics,
          agent: getTierCapabilities("agent").hasVeaAnalytics,
          rep: getTierCapabilities("rep").hasVeaAnalytics,
          vea: getTierCapabilities("vea").hasVeaAnalytics,
        },
      },
    ],
  },
];
